/**
 * Build a human-readable label for conditional logic conditions.
 * e.g. "Employment Status = Employed"
 */
export function buildConditionLabel(conditions, steps) {
    if (!conditions || conditions.length === 0) return '';
    return conditions.map((condition) => {
        const sourceStep = steps.find(s => s.id === condition.source_step_id);
        const sourceField = sourceStep?.fields?.find(f => f.id === condition.source_field_id);
        const fieldName = sourceField?.name || sourceField?.field_label || `Field #${condition.source_field_id}`;
        const operatorLabel = {
            equals: '=', not_equals: '!=',
            in: 'in', not_in: 'not in',
            is_empty: 'is empty', is_not_empty: 'is not empty'
        }[condition.operator] || condition.operator;
        const needsValue = condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty';
        if (needsValue && condition.value != null) {
            return `${fieldName} ${operatorLabel} "${condition.value}"`;
        }
        return `${fieldName} ${operatorLabel}`;
    }).join(' AND ');
}

/**
 * Build short label and full detail for auto-outcome rules.
 */
export function buildAutoOutcomeInfo(autoOutcomeRules, steps) {
    if (!autoOutcomeRules || autoOutcomeRules.length === 0) return { label: null, detail: null };
    const label = 'Auto: ' + autoOutcomeRules.map(r =>
        r.outcome === 'positive' ? 'Complete' : 'Close'
    ).join(', ');
    const detail = autoOutcomeRules.map(rule => {
        const outcomeWord = rule.outcome === 'positive' ? 'Complete' : 'Close';
        const conds = (rule.conditions || []).map(c => {
            const s = steps.find(st => st.id === c.source_step_id);
            const f = s?.fields?.find(fl => fl.id === c.source_field_id);
            const val = c.value != null ? ` "${c.value}"` : '';
            return `${f?.name || 'field'} ${c.operator}${val}`;
        }).join(' & ');
        return `${outcomeWord} when ${conds}`;
    }).join(' | ');
    return { label, detail };
}

/**
 * Compute the new ordered step IDs after moving a step to a new position.
 */
export function computeReorderedStepIds(steps, fromStepId, direction) {
    const sorted = [...steps].sort((a, b) => a.position - b.position);
    const fromIndex = sorted.findIndex(s => s.id === fromStepId);
    if (fromIndex === -1) return sorted.map(s => s.id);

    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= sorted.length) return sorted.map(s => s.id);

    const newOrder = [...sorted];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    return newOrder.map(s => s.id);
}
